/** @jsxImportSource @emotion/react */
import React, { useCallback, useMemo, useState } from 'react';
import { css } from '@emotion/react';
import commonStyles from '../../styles/commonStyles';
import {
  Breadcrumb,
  Button,
  Card,
  Empty,
  Form,
  Input,
  Modal,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { PlusOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import MainLayout from '../../components/common/MainLayout';
import {
  useCreateCropAttributeMutation,
  useDeleteCropAttributeMutation,
  useGetCropAttributesQuery,
} from '../../api/cropsApi';
import type {
  CropAttributeCreatePayload,
  CropAttributeOptionInput,
  CropAttributeSummary,
  CropAttributeScopeType,
} from '../../models/Crop';
import { toastError, toastSuccess } from '../../utils/toast';
import { useAttributeEditor } from '../../components/attributes/useAttributeEditor';
import { EditAttributeModal } from '../../components/attributes/EditAttributeModal';
import { AttributeOptionsEditor } from '../../components/attributes/AttributeOptionsEditor';

const { Title, Paragraph, Text } = Typography;

const styles = {
  titleNoMargin: css`
    margin: 0;
  `,
  paragraphNoMargin: css`
    margin: 0;
  `,
  selectWidth220: css`
    width: 220px;
  `,
  searchMaxWidth: css`
    max-width: 320px;
  `,
};

const AdminAttributesCatalogPage: React.FC = () => {
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [createForm] = Form.useForm();
  const [scopeFilter, setScopeFilter] = useState<CropAttributeScopeType | 'all'>('system');
  const [search, setSearch] = useState('');

  const user = useSelector((state: any) => state.auth.user);
  const userActions = useSelector((state: any) => state.auth.user?.actions || []);
  const adminScopeId = useSelector(
    (state: any) => state.saves?.activeSaveId || ''
  ) as string;

  const canManageCropAttributes = useMemo(() => {
    if (!user) return false;
    if (user.role && ['superadmin', 'superuser'].includes(String(user.role).toLowerCase())) {
      return true;
    }
    if (Array.isArray(userActions)) {
      return (
        userActions.includes('crop_attributes.manage') ||
        userActions.includes('crops.attributes.manage') ||
        userActions.includes('crops.attributes.update')
      );
    }
    return false;
  }, [user, userActions]);

  const attributeQueryParams = useMemo(() => {
    const params: any = { saveId: adminScopeId };
    if (scopeFilter !== 'all') {
      params.scopeType = scopeFilter;
    }
    return params;
  }, [scopeFilter, adminScopeId]);

  const {
    data: attributes = [],
    isFetching: attributesLoading,
    isError: attributesError,
    refetch: refetchAttributes,
  } = useGetCropAttributesQuery(attributeQueryParams, { skip: !adminScopeId });

  const {
    editForm,
    editModalOpen,
    isLoadingAttribute,
    handleCloseEditModal,
    handleEditFormValuesChange,
    handleOpenEditAttribute,
    handleUpdateFarmAttribute,
  } = useAttributeEditor(refetchAttributes);

  const filteredAttributes = useMemo(() => {
    const list = attributes as CropAttributeSummary[];
    if (!search.trim()) return list;
    const normalized = search.trim().toLowerCase();
    return list.filter((attribute) => {
      const nameMatch = attribute.name.toLowerCase().includes(normalized);
      const descriptionMatch = (attribute.description ?? '').toLowerCase().includes(normalized);
      return nameMatch || descriptionMatch;
    });
  }, [attributes, search]);

  const [createAttribute, { isLoading: creatingAttribute }] = useCreateCropAttributeMutation();
  const [deleteAttribute, { isLoading: deletingAttribute }] = useDeleteCropAttributeMutation();

  const handleOpenCreate = () => {
    createForm.resetFields();
    createForm.setFieldsValue({ scopeType: 'system' });
    setCreateModalOpen(true);
  };

  const handleCreateSubmit = async () => {
    try {
      const values = await createForm.validateFields();
      const options: CropAttributeOptionInput[] = (values.options || [])
        .map((item: any, index: number) => {
          const title = item?.title?.trim();
          if (!title) return null;
          const code = (item?.code ?? '').trim() || title;
          return { title, code, sortOrder: index };
        })
        .filter((option): option is CropAttributeOptionInput => option !== null);

      if (!adminScopeId && values.scopeType !== 'system') {
        toastError('Не выбран активный сейв');
        return;
      }
      const payload: CropAttributeCreatePayload = {
        name: values.name.trim(),
        description: values.description?.trim() || null,
        scopeType: values.scopeType ?? 'system',
        options: options.length ? options : null,
        binding: null,
      };

      await createAttribute({ ...payload, saveId: adminScopeId }).unwrap();
      toastSuccess('Атрибут создан');
      setCreateModalOpen(false);
      createForm.resetFields();
      refetchAttributes();
    } catch (error: any) {
      if (!error?.errorFields) {
        toastError(error);
      }
    }
  };

  const handleDelete = useCallback((attribute: CropAttributeSummary) => {
    Modal.confirm({
      title: `Удалить атрибут «${attribute.name}»?`,
      icon: <ExclamationCircleOutlined />,
      content: 'Атрибут и связанные значения будут безвозвратно удалены.',
      okText: 'Удалить',
      cancelText: 'Отмена',
      okButtonProps: { danger: true, loading: deletingAttribute },
      onOk: async () => {
        if (!adminScopeId) {
          toastError('Не выбран активный сейв');
          return;
        }
        try {
          await deleteAttribute({ id: attribute.id, saveId: adminScopeId }).unwrap();
          toastSuccess('Атрибут удалён');
          refetchAttributes();
        } catch (error) {
          toastError(error);
        }
      },
    });
  }, [deleteAttribute, deletingAttribute, refetchAttributes, adminScopeId]);

  const columns: ColumnsType<CropAttributeSummary> = useMemo(
    () => [
      {
        title: 'Название',
        dataIndex: 'name',
        render: (value: string, record) => (
          <Space direction="vertical" size={2}>
            <Text strong>{value}</Text>
            {record.description && <Text type="secondary">{record.description}</Text>}
          </Space>
        ),
      },
      {
        title: 'Тип',
        dataIndex: 'scopeType',
        width: 120,
        render: (value: CropAttributeScopeType | string | undefined, record) => {
          const scopeType = value ?? 'system';
          const color = scopeType === 'save' ? 'green' : 'purple';
          const label = scopeType === 'save' ? 'Атрибут сейва' : 'Системный';
          return (
            <Space direction="vertical" size={2}>
              <Tag color={color}>{label}</Tag>
              {scopeType === 'save' && record.saveId != null && (
                <Text type="secondary">Сейв: {record.saveId}</Text>
              )}
            </Space>
          );
        },
      },
      {
        title: 'Опции',
        render: (_, record) => {
          if (!record.options || record.options.length === 0) {
            return <Text type="secondary">Не заданы</Text>;
          }
          return (
            <Space size={[4, 4]} wrap>
              {record.options.map((option) => (
                <Tag key={`${record.id}-${option.id}`}>
                  {option.code && option.code !== option.title
                    ? `${option.title} (${option.code})`
                    : option.title}
                </Tag>
              ))}
            </Space>
          );
        },
      },
      {
        title: 'Действия',
        width: 160,
        render: (_, record) => (
          <Space>
            <Button type="link" onClick={() => handleOpenEditAttribute(record)}>
              Изменить
            </Button>
            <Button type="link" danger onClick={() => handleDelete(record)}>
              Удалить
            </Button>
          </Space>
        ),
      },
    ],
    [handleDelete, handleOpenEditAttribute],
  );

  if (!canManageCropAttributes) {
    return (
      <MainLayout
        breadcrumb={
          <Breadcrumb
            items={[
              { title: 'Администрирование' },
              { title: 'Каталог атрибутов' },
            ]}
          />
        }
      >
        <Card>
          <Card.Meta
            title="Недостаточно прав"
            description="Управление глобальными атрибутами культур доступно только суперпользователям."
          />
        </Card>
      </MainLayout>
    );
  }

  return (
    <MainLayout
      breadcrumb={
        <Breadcrumb
          items={[
            { title: 'Администрирование' },
            { title: 'Каталог атрибутов' },
          ]}
        />
      }
    >
      <Space direction="vertical" size={16} css={commonStyles.fullWidth}>
        <Card>
          <Space direction="vertical" size={4} css={commonStyles.fullWidth}>
            <Title level={4} css={styles.titleNoMargin}>
              Каталог атрибутов
            </Title>
            <Paragraph type="secondary" css={styles.paragraphNoMargin}>
              Управляйте системными и фермерскими атрибутами, их описанием и предустановленными значениями. Привязка к таксономии осуществляется на отдельном экране.
            </Paragraph>
          </Space>
        </Card>

        <Card>
          <Space direction="vertical" size={12} css={commonStyles.fullWidth}>
            <Space css={commonStyles.fullWidth} align="center" wrap>
              <Select
                value={scopeFilter}
                onChange={(value) => setScopeFilter(value)}
                css={styles.selectWidth220}
                options={[
                  { value: 'system', label: 'Системные атрибуты' },
                  { value: 'farm', label: 'Атрибуты фермы' },
                  { value: 'all', label: 'Все атрибуты' },
                ]}
              />
              <Input.Search
                placeholder="Поиск по названию"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                allowClear
                css={styles.searchMaxWidth}
              />
              <Button type="primary" icon={<PlusOutlined />} onClick={handleOpenCreate}>
                Добавить атрибут
              </Button>
            </Space>

            {attributesLoading ? (
              <Spin />
            ) : attributesError ? (
              <Card type="inner" title="Ошибка">
                Не удалось загрузить атрибуты. Попробуйте обновить страницу.
              </Card>
            ) : filteredAttributes.length === 0 ? (
              <Empty description="Атрибуты не найдены" />
            ) : (
              <Table<CropAttributeSummary>
                rowKey={(record) => String(record.id)}
                dataSource={filteredAttributes}
                columns={columns}
                pagination={false}
              />
            )}
          </Space>
        </Card>
      </Space>

      <Modal
        title="Добавить атрибут"
        open={createModalOpen}
        onOk={handleCreateSubmit}
        onCancel={() => setCreateModalOpen(false)}
        okText="Создать"
        cancelText="Отмена"
        confirmLoading={creatingAttribute}
      >
        <Form form={createForm} layout="vertical" preserve={false}>
          <Form.Item
            label="Название"
            name="name"
            rules={[{ required: true, message: 'Введите название атрибута' }]}
          >
            <Input placeholder="Например, Морозостойкость" />
          </Form.Item>
          <Form.Item label="Описание" name="description">
            <Input.TextArea rows={3} placeholder="Опишите назначение атрибута" allowClear />
          </Form.Item>
          <Form.Item label="Тип" name="scopeType" initialValue="system" rules={[{ required: true, message: 'Выберите тип атрибута' }]}
            tooltip="Системные атрибуты доступны всем; save-атрибуты — только в активном сейве">
            <Select
              options={[
                { value: 'system', label: 'Системный' },
                { value: 'save', label: 'Атрибут сейва' },
              ]}
            />
          </Form.Item>
          <Form.List name="options">
            {(fields, operations) => (
              <AttributeOptionsEditor
                fields={fields}
                operations={operations}
                helperText="Опции (необязательно). Перетащите карточки, чтобы изменить порядок."
                emptyText="Опции не заданы"
                listName={['options']}
              />
            )}
          </Form.List>
        </Form>
      </Modal>

      <EditAttributeModal
        open={editModalOpen}
        onCancel={handleCloseEditModal}
        onOk={handleUpdateFarmAttribute}
        form={editForm}
        onValuesChange={handleEditFormValuesChange}
        loading={isLoadingAttribute}
      />
    </MainLayout>
  );
};

export default AdminAttributesCatalogPage;
